/**
 * Creates a lazy service loader with caching (React.lazy style)
 * @param {function} importFn - A function that returns a dynamic import (e.g., () => import('../api/resourceService.js'))
 * @param {string} exportName - The named export to extract (e.g., 'resourceService')
 * @returns {function} An async function that returns the cached service
 *
 * @example
 * const getResourceService = serviceLoader(() => import('../api/resourceService.js'), 'resourceService');
 * const service = await getResourceService();
 */
export function serviceLoader(importFn, exportName) {
    let instance = null;
    return async () => {
        if (!instance) {
            const module = await importFn();
            instance = exportName ? module[exportName] : module.default;
        }
        return instance;
    };
}

// Pre-configured service loaders for commonly used services
// Core Services
export const getTools = serviceLoader(() => import('./tools.js'), 'tools');
export const getStore = serviceLoader(() => import('./store.js'), 'store');
export const getModalService = serviceLoader(() => import('./modalService.js'), 'modalService');
export const getMockResource = serviceLoader(() => import('./mock-resource.js'), 'mockResource');
export const getLocaleService = serviceLoader(() => import('./localeService.js'), 'localeService');
export const getXrayService = serviceLoader(() => import('../api/xrayService.js'), 'xrayService');

// Session services - use serviceLoader helper for consistency
export const getSessionActivityService = serviceLoader(() => import('./sessionActivityService.js'), 'sessionActivityService');
export const getSessionRenewalService = serviceLoader(() => import('./sessionRenewalService.js'), 'sessionRenewalService');
export const getDebugSessionService = serviceLoader(() => import('./debugSessionService.js'), 'debugSessionService');
export const getPasskeyManager = serviceLoader(() => import('./passkey-manager.js'), 'PasskeyManager');
export const getPasswordManager = serviceLoader(() => import('./password-manager.js'), 'PasswordManager');
export const getPasskeyPromptService = serviceLoader(() => import('./passkey-prompt-service.js'), 'PasskeyPromptService');

// Base API Service - statically imported since all services depend on it
// This prevents Vite warning about mixed static/dynamic imports
import { api } from '../api/api.js';
export const getApi = () => Promise.resolve(api);

// Resource & Content Services
export const getResourceService = serviceLoader(() => import('../api/resourceService.js'), 'resourceService');
export const getCollectionService = serviceLoader(() => import('../api/collectionService.js'), 'collectionService');
export const getSkillService = serviceLoader(() => import('../api/skillService.js'), 'skillService');
export const getTagService = serviceLoader(() => import('../api/tagService.js'), 'tagService');
export const getAttributeService = serviceLoader(() => import('../api/attributeService.js'), 'attributeService');
export const getAttributeDefinitionService = serviceLoader(() => import('../api/attributeDefinitionService.js'), 'attributeDefinitionService');
export const getNamingConventionService = serviceLoader(() => import('../api/namingConventionService.js'), 'namingConventionService');
export const getResourceExtractService = serviceLoader(() => import('../api/resourceExtractService.js'), 'resourceExtractService');
export const getTestGenerationService = serviceLoader(() => import('../api/testGenerationService.js'), 'testGenerationService');
export const getJobService = serviceLoader(() => import('../api/jobService.js'), 'jobService');
export const getBloomsTaxonomyScoreService = serviceLoader(() => import('../api/bloomsTaxonomyScoreService.js'), 'bloomsTaxonomyScoreService');
export const getExtractQualityService = serviceLoader(() => import('../api/extractQualityService.js'), 'extractQualityService');
export const getKeywordService = serviceLoader(() => import('../api/keywordService.js'), 'keywordService');
export const getDescriptionService = serviceLoader(() => import('../api/descriptionService.js'), 'descriptionService');
export const getSummaryService = serviceLoader(() => import('../api/summaryService.js'), 'summaryService');
export const getTitleService = serviceLoader(() => import('../api/titleService.js'), 'titleService');
export const getActionLogService = serviceLoader(() => import('../api/actionLogService.js'), 'actionLogService');

// Migration Console Services — target-centric staging + push to external destinations
export const getMigrationProjectService = serviceLoader(() => import('../api/migrationProjectService.js'), 'migrationProjectService');
export const getTargetService = serviceLoader(() => import('../api/targetService.js'), 'targetService');
export const getMigrationTargetResourceService = serviceLoader(() => import('../api/migrationTargetResourceService.js'), 'migrationTargetResourceService');
export const getPushService = serviceLoader(() => import('../api/pushService.js'), 'pushService');

// User & Authentication Services
export const getAuthService = serviceLoader(() => import('./auth-service.js'), 'AuthService');
export const getUserService = serviceLoader(() => import('../api/userService.js'), 'userService');
export const getPasskeyService = serviceLoader(() => import('../api/passkeyService.js'), 'passkeyService');
export const getImpersonationService = serviceLoader(() => import('../api/impersonationService.js'), 'impersonationService');
export const getInsightsLaunchService = serviceLoader(() => import('../api/insightsLaunchService.js'), 'insightsLaunchService');
export const getPreferencesService = serviceLoader(() => import('../api/preferencesService.js'), 'preferencesService');

// Tenant & Customer Services
export const getCustomerService = serviceLoader(() => import('../api/customerService.js'), 'customerService');
export const getPartnerService = serviceLoader(() => import('../api/partnerService.js'), 'partnerService');
export const getSliceService = serviceLoader(() => import('../api/sliceService.js'), 'sliceService');
export const getWorkspaceService = serviceLoader(() => import('../api/workspaceService.js'), 'workspaceService');
export const getIdentityProviderService = serviceLoader(() => import('../api/identityProviderService.js'), 'identityProviderService');
export const getVerifiedDomainService = serviceLoader(() => import('../api/verifiedDomainService.js'), 'verifiedDomainService');

// Billing & Quota Services
export const getQuotaService = serviceLoader(() => import('../api/quotaService.js'), 'quotaService');
export const getBillingService = serviceLoader(() => import('../api/billingService.js'), 'billingService');
export const getSubscriptionService = serviceLoader(() => import('../api/subscriptionService.js'), 'subscriptionService');
export const getSubscriptionEventService = serviceLoader(() => import('../api/subscriptionEventService.js'), 'subscriptionEventService');
export const getInvoiceRecordService = serviceLoader(() => import('../api/invoiceRecordService.js'), 'invoiceRecordService');
export const getAdminMetricsService = serviceLoader(() => import('../api/adminMetricsService.js'), 'adminMetricsService');

// API Token Lifecycle (METALARK-694)
export const getApiTokenService = serviceLoader(() => import('../api/apiTokenService.js'), 'apiTokenService');

// System & Configuration Services
export const getDashboardService = serviceLoader(() => import('../api/dashboardService.js'), 'dashboardService');
export const getFeatureFlagService = serviceLoader(() => import('../api/featureFlagService.js'), 'featureFlagService');
export const getEnvironmentService = serviceLoader(() => import('../api/environmentService.js'), 'environmentService');
export const getSessionConfigAPI = serviceLoader(() => import('../api/sessionConfigAPI.js'), 'sessionConfigAPI');

// Email Services
export const getEmailSuppressionListService = serviceLoader(() => import('../api/emailSuppressionListService.js'), 'emailSuppressionListService');
export const getEmailTemplateService = serviceLoader(() => import('../api/emailTemplateService.js'), 'emailTemplateService');

// Audit & Logging Services
export const getAuditLogService = serviceLoader(() => import('../api/auditLogService.js'), 'auditLogService');

// Outbound webhook delivery log (METALARK-271) — SYSTEM_ADMIN-only operational view.
export const getWebhookDeliveryService = serviceLoader(() => import('../api/webhookDeliveryService.js'), 'webhookDeliveryService');

// Webhook subscription + rule configuration (METALARK-271) — destinations and
// routing rules for the lifecycle bus. SYSTEM_ADMIN-only here; CUSTOMER_ADMIN
// will see their own customer's subscriptions through the same service when
// the Customer 360 → Webhooks tab lands.
export const getWebhookSubscriptionService = serviceLoader(() => import('../api/webhookSubscriptionService.js'), 'webhookSubscriptionService');

// Admin → Stripe Setup (METALARK-671) — SYSTEM_ADMIN view of the four Stripe
// SSM params + EventBridge partner-bus rule presence + a "Reconcile rules"
// button. Companion to webhook-subscriptions but for the Stripe-incoming side.
export const getAdminStripeSetupService = serviceLoader(() => import('../api/adminStripeSetupService.js'), 'adminStripeSetupService');

// Admin → Repository Providers Setup: read-only SSM status for each repository
// provider's OAuth client credentials (Google Drive, SharePoint, Dropbox, Box).
export const getAdminRepositoryProvidersSetupService = serviceLoader(() => import('../api/adminRepositoryProvidersSetupService.js'), 'adminRepositoryProvidersSetupService');

// Admin API Services
export const getRepositoryService = serviceLoader(() => import('../api/admin/repositoryService.js'), 'repositoryService');
export const getDashboardActivityService = serviceLoader(() => import('../api/admin/activityDashboardService.js'), 'activityDashboardService');

// Marketing Campaigns — SYSTEM_ADMIN CRUD + public resolve for the signup form
export const getCampaignService = serviceLoader(() => import('../api/campaignService.js'), 'campaignService');
